import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Edit3, Mail, Phone, Clock } from "lucide-react";
import { norm } from "@/lib/normalize";
import { useLookups } from "@/context/LookupContext";
import { Checkbox } from "@/components/ui/checkbox";

const emptyStaff = { firstName: "", lastName: "", email: "", designation: "", phone: "", bio: "", roleId: "", hireDate: "", gender: "", bookable: true };

const DAYS = [
  { key: "MONDAY", label: "MONDAY" },
  { key: "TUESDAY", label: "TUESDAY" },
  { key: "WEDNESDAY", label: "WEDNESDAY" },
  { key: "THURSDAY", label: "THURSDAY" },
  { key: "FRIDAY", label: "FRIDAY" },
  { key: "SATURDAY", label: "Saturday" },
  { key: "SUNDAY", label: "SUNDAY" },
];

const DEFAULT_HOURS = DAYS.reduce((a, d) => ({ ...a, [d.key]: { startTime: "09:00", endTime: "21:00", working: d.key !== "SUNDAY" } }), {});

function HoursEditor({ staffId, isAdmin }) {
  const { lookups } = useLookups();

  const [hours, setHours] = useState(DEFAULT_HOURS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/staff/${staffId}/hours`)
      .then((r) => {
        if (!alive) return;
        const raw = r.data || [];
        // Handle both array response and object with schedule/leaveDates
        const schedule = Array.isArray(raw) ? raw : (Array.isArray(raw.schedule) ? raw.schedule : raw.hours || []);
        const mapped = Array.isArray(schedule)
          ? schedule.reduce((acc, item) => ({
              ...acc,
              [item.dayOfWeek]: {
                id: item.id,
                staffId: item.staffId,
                startTime: item.startTime ? item.startTime.slice(0, 5) : "09:00",
                endTime: item.endTime ? item.endTime.slice(0, 5) : "21:00",
                working: item.working ?? item.isWorking ?? true,
              }
            }), {})
          : {};
        setHours({ ...DEFAULT_HOURS, ...mapped });
      })
      .catch(() => toast.error("Failed to load hours"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [staffId]);

  
  const setDay = (key, patch) => setHours((h) => ({ ...h, [key]: { ...h[key], ...patch } }));
  

  const save = async () => {
    setSaving(true);
    try {
      // Transform object back into array for the backend
      const payload = DAYS.map((d) => {
        const h = hours[d.key] || {};
        return {
          ...(h.id ? { id: h.id } : {}),
          staffId: staffId,
          dayOfWeek: d.key,
          startTime: h.startTime || null,
          endTime: h.endTime || null,
          working: h.working ?? false,
        };
      });
      // Use POST for new schedule (no ids), PUT for updating existing
      const hasExisting = payload.some((item) => item.id);
      if (hasExisting) {
        await api.put(`/staff/${staffId}/hours`, payload);
      } else {
        await api.post(`/staff/${staffId}/hours`, payload);
      }
      toast.success("Working hours saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-sm text-emerald-muted py-4">Loading hours…</div>;

  return (
    <div className="space-y-3" data-testid={`hours-editor-${staffId}`}>
      {DAYS.map((d) => {
        const h = hours[d.key] || { startTime: "09:00", endTime: "21:00", working: false };
        return (
          <div key={d.key} className="grid grid-cols-12 gap-3 items-center bg-emerald-elev/40 border border-gold-subtle rounded-md px-3 py-2"
            data-testid={`hours-row-${staffId}-${d.key}`}>
            <div className="col-span-3 text-white text-sm">{d.label}</div>
            <div className="col-span-3">
              <Input type="time" value={h.startTime} disabled={!isAdmin || !h.working}
                onChange={(e) => setDay(d.key, { startTime: e.target.value })}
                className="bg-emerald-elev border-gold-subtle text-white h-9"
                data-testid={`hours-${staffId}-${d.key}-start`} />
            </div>
            <div className="col-span-1 text-center text-emerald-muted text-xs">to</div>
            <div className="col-span-3">
              <Input type="time" value={h.endTime} disabled={!isAdmin || !h.working}
                onChange={(e) => setDay(d.key, { endTime: e.target.value })}
                className="bg-emerald-elev border-gold-subtle text-white h-9"
                data-testid={`hours-${staffId}-${d.key}-end`} />
            </div>
            <div className="col-span-2 flex items-center justify-end gap-2">
              <Switch checked={h.working} disabled={!isAdmin}
                onCheckedChange={(v) => setDay(d.key, { working: !!v })}
                data-testid={`hours-${staffId}-${d.key}-closed`} />
              <span className="text-xs text-emerald-muted">{h.working ? "On" : "Off"}</span>
            </div>
          </div>
        );
      })}
      {isAdmin && (
        <button onClick={save} disabled={saving}
          className="gold-button px-5 py-2 rounded-md mt-2 disabled:opacity-60"
          data-testid={`save-hours-${staffId}`}>
          {saving ? "Saving…" : "Save Working Hours"}
        </button>
      )}
    </div>
  );
}

export default function Staff() {
  const { user } = useAuth();
  const { lookups } = useLookups();
  const staffRoleId = (lookups?.roles || []).find((r) => (r.name || "").toLowerCase() === "staff")?.id || "";
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...emptyStaff, roleId: staffRoleId });
  const [editId, setEditId] = useState(null);
  const [activeHoursStaff, setActiveHoursStaff] = useState("");
  const isAdmin = norm(user.roleName) === "admin";
  const canEdit = norm(user.roleName) === "admin" || norm(user.roleName) === "receptionist";

  const load = async () => {
    try {
      const r = await api.get("/staff");
      setRows(r.data);
      if (r.data.length && !activeHoursStaff) setActiveHoursStaff(r.data[0].id);
    } catch { toast.error("Failed"); }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/staff/${editId}`, form);
      else await api.post("/staff", form);
      toast.success("Saved"); setOpen(false); load();
    } catch { toast.error("Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this stylist?")) return;
    try { await api.delete(`/staff/${id}`); toast.success("Removed"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-8" data-testid="staff-page">
      <div className="flex items-end justify-between gap-4">
        <div><div className="overline mb-2">Team</div><h1 className="font-heading text-4xl text-white">Stylists & Therapists</h1></div>
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button onClick={() => { setForm({ ...emptyStaff, roleId: staffRoleId }); setEditId(null); }} data-testid="add-staff-btn" className="gold-button px-5 py-2.5 rounded-full inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Add Staff</button>
            </DialogTrigger>
            <DialogContent className="bg-emerald-surface border-gold-subtle text-white max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">{editId ? "Edit" : "New"} staff</DialogTitle>
                <DialogDescription className="text-emerald-muted">Enter stylist details.</DialogDescription>
              </DialogHeader>
              <form onSubmit={save} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>First Name</Label><Input required value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} className="bg-emerald-elev border-gold-subtle mt-1" data-testid="staff-name-input" /></div>
                  <div><Label>Last Name</Label><Input value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} className="bg-emerald-elev border-gold-subtle mt-1" data-testid="staff-lastname-input" /></div>
                </div>
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-emerald-elev border-gold-subtle mt-1" /></div>
                <div><Label>Specialty</Label><Input required value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} className="bg-emerald-elev border-gold-subtle mt-1" /></div>
                <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-emerald-elev border-gold-subtle mt-1" /></div>
                <div><Label>Bio</Label><Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="bg-emerald-elev border-gold-subtle mt-1" /></div>
                <div><Label>Hire Date</Label><Input type="date" value={form.hireDate} onChange={(e) => setForm({ ...form, hireDate: e.target.value })} className="bg-emerald-elev border-gold-subtle mt-1 text-white" /></div>
                <div>
                  <Label>Gender</Label>
                  <Select value={form.gender} onValueChange={(val) => setForm({ ...form, gender: val })}>
                    <SelectTrigger className="mt-1 bg-emerald-elev border-gold-subtle text-white h-9">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MALE">Male</SelectItem>
                      <SelectItem value="FEMALE">Female</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <Checkbox id="bookable" checked={form.bookable} onCheckedChange={(v) => setForm({ ...form, bookable: !!v })} />
                  <Label htmlFor="bookable" className="text-sm cursor-pointer">Bookable (available for appointments)</Label>
                </div>
                <button type="submit" className="gold-button w-full h-11 rounded-md">Save</button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Tabs defaultValue="team" className="w-full">
        <TabsList className="bg-emerald-surface border border-gold-subtle" data-testid="staff-tabs">
          <TabsTrigger value="team" data-testid="tab-team">Team</TabsTrigger>
          <TabsTrigger value="hours" data-testid="tab-hours"><Clock className="w-3 h-3 mr-1 text-gold" /> Working Hours</TabsTrigger>
        </TabsList>

        <TabsContent value="team" className="mt-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {rows.map((s) => (
              <div key={s.id} className="bg-emerald-surface border border-gold-subtle rounded-2xl p-6" data-testid={`staff-card-${s.id}`}>
                <div className="w-14 h-14 rounded-full bg-gold text-emerald-950 flex items-center justify-center font-heading text-2xl">
                  {s.firstName.split(" ").map((x) => x[0]).slice(0, 2).join("")}
                </div>
                <h3 className="font-heading text-2xl text-white mt-4">{s.firstName}</h3>
                <div className="overline mt-1">{s.designation}{s.gender ? ` (${s.gender})` : ""}</div>
                <p className="text-sm text-emerald-muted mt-3">{s.bio}</p>
                <div className="mt-4 space-y-1 text-xs text-emerald-muted">
                  <div className="flex items-center gap-2"><Mail className="w-3 h-3" /> {s.email}</div>
                  {s.phone && <div className="flex items-center gap-2"><Phone className="w-3 h-3" /> {s.phone}</div>}
                </div>
                {canEdit && (
                  <div className="mt-4 pt-4 border-t border-gold-subtle flex gap-2">
                    <button onClick={() => { const parts = (s.firstName || "").split(" "); setForm({ firstName: parts[0] || "", lastName: parts.slice(1).join(" ") || s.lastName || "", email: s.email, designation: s.designation, phone: s.phone || "", bio: s.bio || "", roleId: s.roleId || emptyStaff.roleId, hireDate: s.hireDate || "", gender: s.gender || "", bookable: s.bookable ?? true }); setEditId(s.id); setOpen(true); }} className="text-xs text-emerald-muted hover:text-gold inline-flex items-center gap-1"><Edit3 className="w-3 h-3" /> Edit</button>
                    <button onClick={() => del(s.id)} className="text-xs text-emerald-muted hover:text-red-300 inline-flex items-center gap-1 ml-auto" data-testid={`delete-staff-${s.id}`}><Trash2 className="w-3 h-3" /> Remove</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="hours" className="mt-6">
          {rows.length === 0 && <div className="text-emerald-muted">No staff yet.</div>}
          {rows.length > 0 && (
            <div className="grid lg:grid-cols-[220px_1fr] gap-6">
              <div className="bg-emerald-surface border border-gold-subtle rounded-2xl p-2 h-fit">
                {rows.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setActiveHoursStaff(s.id)}
                    data-testid={`hours-staff-tab-${s.id}`}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm transition ${
                      activeHoursStaff === s.id
                        ? "bg-emerald-elev border border-gold text-white font-medium"
                        : "text-emerald-muted hover:text-white hover:bg-emerald-elev/60"
                    }`}
                  >
                    {s.firstName}
                  </button>
                ))}
              </div>
              <div className="bg-emerald-surface border border-gold-subtle rounded-2xl p-6">
                <div className="overline mb-4">Weekly Schedule · {rows.find((s) => s.id === activeHoursStaff)?.firstName || ""}</div>
                {activeHoursStaff && <HoursEditor staffId={activeHoursStaff} isAdmin={canEdit} />}
                {!canEdit && <p className="text-xs text-emerald-muted mt-3">Only admins can edit working hours.</p>}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}